import {
  create_requestor_application_journal,
  fetch_requestor_application_journals,
  fetch_requestor_entity_profiles,
} from '@/services/requestor_portal_service';
import BlockingLoaderOverlay from '@/components/config_components/BlockingLoaderOverlay/BlockingLoaderOverlay.vue';
import { fetch_case_owner_cases } from '@/views/x-verdict_views/CaseOwner/AllCases/owner_case_queue_service';
import { read_session_project_id } from '@/views/x-verdict_views/CompanyAdmin/CompanyCases/company_cases_common';
import {
  journal_activity,
  journal_hours,
  journal_is_billable,
  journal_user_name,
  month_key,
  month_label,
  normalize_text,
} from '@/views/x-verdict_views/CompanyAdmin/TimeSheets/time_sheets_formatters';

const HANDLER_RATE = 850;
const PENDING_TIMESHEET_STORAGE_PREFIX = 'owner_time_entry_pending';
const DRAFT_TIMESHEET_ROWS_STORAGE_PREFIX = 'owner_time_entry_rows';
const REVIEW_NOTE_PREFIX = 'Timesheet Review';
const CASE_CODE_FIELDS = ['case_code', 'application_code', 'reference'];
const CASE_TITLE_FIELDS = ['title', 'entity_profile_name', 'name', 'description'];
const EMPTY_SUMMARY = {
  billable_hours: '0h',
  billable_meta: '@ R 850 - R 0',
  non_billable_hours: '0h',
  non_billable_meta: 'Admin - training - internal',
  total_hours: '0h',
  total_meta: '0% billable',
  rate_label: 'Your rate',
  rate_value: 'R850/hr'
};

function start_of_week(date = new Date()) {
  const result = new Date(date);
  const day = result.getDay() || 7;
  result.setHours(0, 0, 0, 0);
  result.setDate(result.getDate() - day + 1);
  return result;
}

function add_days(date, days) {
  const next_date = new Date(date);
  next_date.setDate(next_date.getDate() + days);
  return next_date;
}

function same_day(left, right) {
  return left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate();
}

function week_dates(week_start) {
  return Array.from({ length: 7 }, (_, index) => add_days(week_start, index));
}

function date_key(date) {
  return new Date(date).toISOString().slice(0, 10);
}

function same_week(left, right) {
  return date_key(start_of_week(left)) === date_key(start_of_week(right));
}

function format_column_day(date) {
  return date.toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric' });
}

function format_range_label(week_start) {
  const week_end = add_days(week_start, 6);
  return `${week_start.toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' })} - ${week_end.toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' })}`;
}

function format_week_label(week_start) {
  return format_range_label(week_start);
}

function format_hours(value = 0) {
  return `${Number(value.toFixed(1))}h`;
}

function format_cell_hours(value = 0) {
  if (!value) return '';
  return Number(value.toFixed(1)).toString().replace('.', ',');
}

function format_currency(value = 0) {
  return Number(value || 0)
    .toLocaleString('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      maximumFractionDigits: 0,
    })
    .replace('ZAR', 'R')
    .trim();
}

function journal_date_value(journal = {}) {
  const raw_value = journal?.journal_date || journal?.date_created || journal?.created_at;
  const date = new Date(raw_value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function matches_current_handler(component, journal = {}) {
  const current_user_id = Number(component.$store?.getters?.currentUserId || 0);
  const journal_user_id = Number(journal?.user_id || journal?.created_by_id || journal?.owner_id || 0);
  if (current_user_id && journal_user_id) {
    return current_user_id === journal_user_id;
  }

  const current_lead_name = normalize_text(component.$store?.getters?.currentLeadName || '').toLowerCase();
  const journal_owner = normalize_text(journal_user_name(journal)).toLowerCase();
  return Boolean(current_lead_name) && current_lead_name === journal_owner;
}

function number_value(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function application_id_from_profile(profile = {}) {
  return number_value(
    profile.application_id
    || profile.latest_application_id
    || profile.current_application_id
  );
}

function profile_field(profile = {}, field = '') {
  const entry = Object.entries(profile).find(([key]) => key === field);
  return entry ? entry[1] : '';
}

function first_present(values = []) {
  return values.find((value) => value !== undefined && value !== null && value !== '') || '';
}

function profile_case_code(profile = {}) {
  return normalize_text(first_present(CASE_CODE_FIELDS.map((field) => profile_field(profile, field))))
    || `APP-${application_id_from_profile(profile) || 'N/A'}`;
}

function profile_case_title(profile = {}) {
  return normalize_text(first_present(CASE_TITLE_FIELDS.map((field) => profile_field(profile, field))))
    || 'Untitled case';
}

function row_key(case_item = {}, billable = true, activity_label = '') {
  const base_id = case_item?.application_id || case_item?.case_code || case_item?.title || 'case';
  const activity_key = normalize_text(activity_label).toLowerCase().replace(/\s+/g, '-');
  return `${billable ? 'billable' : 'non-bill'}-${base_id}${activity_key ? `-${activity_key}` : ''}`;
}

function build_row(case_item = {}, billable = true, options = {}) {
  const activity_label = normalize_text(options.activity_label || case_item?.title || 'Time entry');
  const display_title = options.display_title || case_item?.title || 'Untitled case';
  return {
    key: row_key(case_item, billable, activity_label),
    application_id: case_item?.application_id || null,
    case_code: case_item?.case_code || '',
    code: case_item?.case_code || 'Case',
    title: display_title,
    activity_label,
    billable,
    tag: billable ? 'Billable' : 'Non-bill',
    tag_class: billable ? 'case-owner-timesheet-tag-billable' : 'case-owner-timesheet-tag-nonbill',
  };
}

function historical_case_item(profile = {}, assigned_case = null) {
  if (assigned_case?.application_id) {
    return assigned_case;
  }

  return {
    application_id: application_id_from_profile(profile),
    case_code: profile_case_code(profile),
    title: profile_case_title(profile),
  };
}

function normalize_entry(row = {}, journal = {}) {
  const date = journal_date_value(journal);
  if (!date) return null;

  const hours_value = Number(journal_hours(journal) || 0);
  if (!(hours_value > 0)) return null;

  return {
    row_key: row.key,
    date,
    hours_value,
    billable: row.billable,
  };
}

function entry_sum_for_row_day(entries = [], key, date) {
  return entries
    .filter((entry) => entry.row_key === key && same_day(entry.date, date))
    .reduce((total, entry) => total + Number(entry.hours_value || 0), 0);
}

function pending_sum_for_row_day(pending_entries = {}, key, date) {
  return Number(pending_entries?.[`${key}|${date_key(date)}`] || 0);
}

function build_rows(row_definitions = [], existing_entries = [], pending_entries = {}, week_start) {
  const dates = week_dates(week_start);

  return row_definitions.map((row) => {
    const numeric_values = dates.map((date) =>
      entry_sum_for_row_day(existing_entries, row.key, date) + pending_sum_for_row_day(pending_entries, row.key, date)
    );
    const values = numeric_values.map((value) => format_cell_hours(value));
    const total_value = dates.reduce((sum, date) => sum
      + entry_sum_for_row_day(existing_entries, row.key, date)
      + pending_sum_for_row_day(pending_entries, row.key, date), 0);

    return {
      ...row,
      numeric_values,
      total_value,
      values,
      total: format_hours(total_value),
    };
  });
}

function build_day_totals(row_definitions = [], existing_entries = [], pending_entries = {}, week_start) {
  return week_dates(week_start).map((date) => {
    const total = row_definitions.reduce((sum, row) => sum
      + entry_sum_for_row_day(existing_entries, row.key, date)
      + pending_sum_for_row_day(pending_entries, row.key, date), 0);
    return format_hours(total);
  });
}

function display_entries(row_definitions = [], existing_entries = [], pending_entries = {}, week_start) {
  const dates = week_dates(week_start);
  const row_map = new Map(row_definitions.map((row) => [row.key, row]));
  const pending_display_entries = Object.entries(pending_entries)
    .map(([key, value]) => {
      const [row_key_value, date_value] = key.split('|');
      const row = row_map.get(row_key_value);
      const date = new Date(date_value);
      if (!row || Number.isNaN(date.getTime()) || !(value > 0)) return null;
      return {
        row_key: row.key,
        billable: row.billable,
        hours_value: Number(value),
        date,
      };
    })
    .filter(Boolean)
    .filter((entry) => dates.some((date) => same_day(date, entry.date)));

  return [...existing_entries, ...pending_display_entries];
}

function build_detail_summary_from_rows(rows = []) {
  const billable_hours_value = rows
    .filter((row) => row.billable)
    .reduce((total, row) => total + Number(row.total_value || 0), 0);
  const non_billable_hours_value = rows
    .filter((row) => !row.billable)
    .reduce((total, row) => total + Number(row.total_value || 0), 0);
  const total_hours_value = billable_hours_value + non_billable_hours_value;
  const billable_percent = total_hours_value
    ? Math.round((billable_hours_value / total_hours_value) * 100)
    : 0;

  return {
    billable_hours: format_hours(billable_hours_value),
    billable_meta: `@ R 850 - ${format_currency(billable_hours_value * HANDLER_RATE)}`,
    non_billable_hours: format_hours(non_billable_hours_value),
    non_billable_meta: 'Admin - training - internal',
    total_hours: format_hours(total_hours_value),
    total_meta: `${billable_percent}% billable`,
    rate_label: 'Your rate',
    rate_value: 'R850/hr'
  };
}

function build_metrics_from_rows(rows = [], week_start) {
  const weekdays = week_dates(week_start).slice(0, 5);
  const totals_by_day = weekdays.map((_, day_index) =>
    rows.reduce((sum, row) => sum + Number(row.numeric_values?.[day_index] || 0), 0)
  );
  const billable_hours_value = rows
    .filter((row) => row.billable)
    .reduce((total, row) => total + Number(row.total_value || 0), 0);
  const total_hours_value = rows.reduce((total, row) => total + Number(row.total_value || 0), 0);
  const billable_rate = total_hours_value ? Math.round((billable_hours_value / total_hours_value) * 100) : 0;
  const missing_days = weekdays.filter((_, index) => Number(totals_by_day[index] || 0) <= 0);

  return [
    { label: 'Week total', value: format_hours(total_hours_value), change: 'Target 35h' },
    { label: 'Billable', value: format_hours(billable_hours_value), change: `${billable_rate}% billable rate` },
    { label: 'Missing days', value: String(missing_days.length), change: missing_days.length ? missing_days.map((date) => date.toLocaleDateString('en-ZA', { weekday: 'short' })).join(', ') : 'None' },
    { label: 'Status', value: 'Draft', change: `Submit by ${add_days(week_start, 6).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' })}` }
  ];
}

function review_event_from_journal(journal = {}) {
  const description = normalize_text(journal.journal_description);
  if (!description.startsWith(REVIEW_NOTE_PREFIX)) return null;

  const metadata = description
    .split('|')
    .map((part) => part.trim())
    .slice(1)
    .reduce((result, part) => {
      const [raw_key, ...value_parts] = part.split(':');
      const key = normalize_text(raw_key).toLowerCase().replace(/\s+/g, '_');
      const value = normalize_text(value_parts.join(':'));
      if (key) result[key] = value;
      return result;
    }, {});

  const normalized_status = normalize_text(metadata.status).toLowerCase();
  const status_key = normalized_status === 'approved'
    ? 'approved'
    : ['disputed', 'returned', 'returned for revision', 'revision', 'returned-for-revision'].includes(normalized_status)
      ? 'disputed'
      : '';
  const week_value = metadata.week;
  const review_week_start = week_value ? start_of_week(new Date(week_value)) : null;
  if (!status_key || !review_week_start || Number.isNaN(review_week_start.getTime())) {
    return null;
  }

  return {
    status_key,
    week_start: review_week_start,
    comment: metadata.comment || '',
    created_at: journal.date_created || journal.created_at || journal.journal_date,
  };
}

function week_status_state({ selected_week_start, rows = [], pending_entries = {}, existing_entries = [], review_events = [] } = {}) {
  const has_pending_drafts = Object.keys(pending_entries || {}).some((key) => {
    const [, raw_date] = String(key).split('|');
    const pending_date = new Date(raw_date);
    return !Number.isNaN(pending_date.getTime()) && same_week(pending_date, selected_week_start);
  });

  if (has_pending_drafts) {
    return {
      value: 'Draft',
      change: `Submit by ${add_days(selected_week_start, 6).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' })}`,
    };
  }

  const week_entries = existing_entries.filter((entry) => same_week(entry.date, selected_week_start));
  const has_logged_hours = rows.some((row) => Number(row.total_value || 0) > 0) || week_entries.some((entry) => Number(entry.hours_value || 0) > 0);

  if (!has_logged_hours) {
    return {
      value: 'Draft',
      change: `Submit by ${add_days(selected_week_start, 6).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' })}`,
    };
  }

  const latest_submission_at = week_entries
    .filter((entry) => Number(entry.hours_value || 0) > 0)
    .map((entry) => new Date(entry.date).getTime())
    .sort((left, right) => right - left)[0] || 0;

  const matching_reviews = review_events
    .filter((event) => same_week(event.week_start, selected_week_start))
    .sort((left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime());
  const latest_review = matching_reviews[0] || null;
  const latest_review_at = latest_review ? new Date(latest_review.created_at).getTime() : 0;

  if (!latest_review || latest_submission_at > latest_review_at) {
    return {
      value: 'Pending review',
      change: 'Submitted and awaiting approval',
    };
  }

  if (latest_review.status_key === 'approved') {
    return {
      value: 'Ready for billing',
      change: 'Approved and ready to invoice',
    };
  }

  return {
    value: 'Revision needed',
    change: latest_review.comment || 'Returned for revision',
  };
}

function status_badge_class(status_key = '') {
  if (status_key === 'approved') {
    return 'bg-success-subtle text-success-emphasis border border-success-subtle';
  }
  if (status_key === 'disputed') {
    return 'bg-danger-subtle text-danger-emphasis border border-danger-subtle';
  }
  return 'bg-warning-subtle text-warning-emphasis border border-warning-subtle';
}

function build_submitted_timesheets(existing_entries = [], review_events = []) {
  const grouped_entries = existing_entries.reduce((groups, entry) => {
    const week_key = date_key(start_of_week(entry.date));
    const next_group = groups.get(week_key) || [];
    next_group.push(entry);
    groups.set(week_key, next_group);
    return groups;
  }, new Map());

  return Array.from(grouped_entries.entries())
    .map(([week_key, entries]) => {
      const week_start = start_of_week(new Date(week_key));
      const latest_submission_at = entries
        .map((entry) => new Date(entry.date).getTime())
        .sort((left, right) => right - left)[0] || 0;
      const matching_reviews = review_events
        .filter((event) => same_week(event.week_start, week_start))
        .sort((left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime());
      const latest_review = matching_reviews[0] || null;
      const latest_review_at = latest_review ? new Date(latest_review.created_at).getTime() : 0;
      const billable_value = entries
        .filter((entry) => entry.billable)
        .reduce((total, entry) => total + Number(entry.hours_value || 0), 0);
      const non_billable_value = entries
        .filter((entry) => !entry.billable)
        .reduce((total, entry) => total + Number(entry.hours_value || 0), 0);
      const total_value = billable_value + non_billable_value;
      const cases = new Set(entries.map((entry) => String(entry.row_key || '')).filter(Boolean)).size;

      const state = !latest_review || latest_submission_at > latest_review_at
        ? {
          status_key: 'pending',
          status: 'Pending review',
          change: 'Submitted and awaiting approval',
          comment: '',
        }
        : latest_review.status_key === 'approved'
          ? {
            status_key: 'approved',
            status: 'Ready for billing',
            change: 'Approved and ready to invoice',
            comment: latest_review.comment || '',
          }
          : {
            status_key: 'disputed',
            status: 'Revision needed',
            change: 'Returned for revision',
            comment: latest_review.comment || '',
          };

      return {
        id: week_key,
        week_key,
        week_start,
        month_key: month_key(week_start),
        month_label: month_label(week_start),
        week_label: format_week_label(week_start),
        billable_hours: format_hours(billable_value),
        non_billable_hours: format_hours(non_billable_value),
        total_hours: format_hours(total_value),
        amount: format_currency(billable_value * HANDLER_RATE),
        cases: String(cases),
        status: state.status,
        status_key: state.status_key,
        status_badge_class: status_badge_class(state.status_key),
        change: state.change,
        comment: state.comment,
        reviewed_at: latest_review?.created_at || '',
      };
    })
    .sort((left, right) => right.week_start.getTime() - left.week_start.getTime());
}

function draft_payloads(component) {
  return Object.entries(component.pending_entries)
    .map(([key, hours]) => {
      const [row_key_value, journal_date] = key.split('|');
      const row = component.row_definitions.find((candidate) => candidate.key === row_key_value);
      const evidence_files = Array.isArray(component.pending_evidence?.[key])
        ? component.pending_evidence[key]
        : [];
      return row && Number(hours) > 0
        ? {
          application_id: row.application_id,
          user_id: Number(component.$store?.getters?.currentUserId || 0),
          journal_description: `${row.billable ? 'Billable' : 'Not Billable'} - ${row.activity_label}${review_suffix(component.submit_review_target)}`,
          hours_spent: Number(hours).toFixed(2),
          journal_date,
          evidence_files,
          evidence_names: evidence_files.map((file) => file?.name || 'Evidence'),
        }
        : null;
    })
    .filter(Boolean);
}

function review_suffix(review_target = '') {
  const label = review_target_label(review_target);
  return label ? ` | Submit for review: ${label}` : '';
}

function review_target_label(review_target = '') {
  return review_target === 'system-admin' ? 'System Admin' : 'Coordinator';
}

function pending_storage_key(project_id = null) {
  return `${PENDING_TIMESHEET_STORAGE_PREFIX}:${project_id || 'default'}`;
}

function draft_rows_storage_key(project_id = null) {
  return `${DRAFT_TIMESHEET_ROWS_STORAGE_PREFIX}:${project_id || 'default'}`;
}

function read_pending_entries(project_id = null) {
  if (typeof window === 'undefined') return {};

  try {
    const raw_value = window.sessionStorage.getItem(pending_storage_key(project_id));
    const parsed = raw_value ? JSON.parse(raw_value) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function write_pending_entries(project_id = null, pending_entries = {}) {
  if (typeof window === 'undefined') return;

  const has_entries = Object.keys(pending_entries || {}).length > 0;
  if (!has_entries) {
    window.sessionStorage.removeItem(pending_storage_key(project_id));
    return;
  }

  window.sessionStorage.setItem(
    pending_storage_key(project_id),
    JSON.stringify(pending_entries)
  );
}

function read_draft_rows(project_id = null) {
  if (typeof window === 'undefined') return [];

  try {
    const raw_value = window.sessionStorage.getItem(draft_rows_storage_key(project_id));
    const parsed = raw_value ? JSON.parse(raw_value) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function write_draft_rows(project_id = null, rows = []) {
  if (typeof window === 'undefined') return;

  const normalized_rows = Array.isArray(rows) ? rows.filter((row) => row?.key) : [];
  if (!normalized_rows.length) {
    window.sessionStorage.removeItem(draft_rows_storage_key(project_id));
    return;
  }

  window.sessionStorage.setItem(
    draft_rows_storage_key(project_id),
    JSON.stringify(normalized_rows)
  );
}

export default {
  name: 'OwnerTimeEntry',
  components: {
    BlockingLoaderOverlay,
  },
  data: () => ({
    active_tab: 'entry',
    selected_month: '',
    selected_week_start: start_of_week(new Date()),
    case_rows: [],
    extra_rows: [],
    existing_entries: [],
    pending_entries: {},
    pending_evidence: {},
    review_events: [],
    loading_entries: false,
    entries_error: '',
    active_modal: '',
    add_case_row_requested: false,
    feedback_modal: {
      title: '',
      message: '',
      tone: 'info',
    },
    editing_cell: {
      row: null,
      date: null,
      key: '',
      existing_hours: 0,
      pending_hours: 0,
      input: '',
    },
    add_case_form: {
      application_id: '',
      activity: '',
      billable: false,
      day_key: '',
      hours: '',
      evidence_files: [],
    },
    submit_review_target: 'coordinator',
    submitting_week: false,
  }),
  created() {
    this.reset_modal_state();
    this.load_time_entries();
  },
  computed: {
    project_id() {
      return this.$route?.query?.project_id
        || this.$route?.query?.projectId
        || read_session_project_id();
    },
    columns() {
      return week_dates(this.selected_week_start).map(format_column_day);
    },
    row_definitions() {
      return [...this.case_rows, ...this.extra_rows];
    },
    range_label() {
      return format_range_label(this.selected_week_start);
    },
    week_display_entries() {
      return display_entries(
        this.row_definitions,
        this.existing_entries,
        this.pending_entries,
        this.selected_week_start
      );
    },
    detail_summary() {
      return this.rows.length ? build_detail_summary_from_rows(this.rows) : EMPTY_SUMMARY;
    },
    metrics() {
      const status = week_status_state({
        selected_week_start: this.selected_week_start,
        rows: this.rows,
        pending_entries: this.pending_entries,
        existing_entries: this.existing_entries,
        review_events: this.review_events,
      });
      return [
        ...build_metrics_from_rows(this.rows, this.selected_week_start).slice(0, 3),
        { label: 'Status', value: status.value, change: status.change },
      ];
    },
    submitted_timesheets() {
      return build_submitted_timesheets(this.existing_entries, this.review_events);
    },
    months() {
      const month_map = this.submitted_timesheets.reduce((months, sheet) => {
        if (sheet.month_key && !months.has(sheet.month_key)) {
          months.set(sheet.month_key, sheet.month_label || sheet.month_key);
        }
        return months;
      }, new Map());

      return Array.from(month_map.entries())
        .sort(([left], [right]) => right.localeCompare(left))
        .map(([value, label]) => ({ value, label }));
    },
    selected_month_label() {
      return this.months.find((month) => month.value === this.selected_month)?.label || this.selected_month || '';
    },
    month_scoped_submitted_timesheets() {
      return this.selected_month
        ? this.submitted_timesheets.filter((sheet) => sheet.month_key === this.selected_month)
        : this.submitted_timesheets;
    },
    selected_week_submitted_sheet() {
      return this.submitted_timesheets.find((sheet) => same_week(sheet.week_start, this.selected_week_start)) || null;
    },
    rows() {
      return build_rows(
        this.row_definitions,
        this.existing_entries,
        this.pending_entries,
        this.selected_week_start
      );
    },
    day_totals() {
      return build_day_totals(
        this.row_definitions,
        this.existing_entries,
        this.pending_entries,
        this.selected_week_start
      );
    },
    submit_review_target_label() {
      return review_target_label(this.submit_review_target);
    },
    selectable_cases() {
      return this.case_rows.map((row) => ({
        application_id: row.application_id,
        case_code: row.case_code,
        title: row.title,
      }));
    },
    selectable_week_days() {
      return week_dates(this.selected_week_start).map((date) => ({
        key: date_key(date),
        label: format_column_day(date),
      }));
    },
    should_render_active_modal() {
      if (!this.active_modal) {
        return false;
      }

      if (this.active_modal === 'add-case-row') {
        return this.add_case_row_requested;
      }

      return true;
    },
    page_loading_message() {
      return 'Loading time entry...';
    },
    page_loading_detail() {
      return 'Pulling your assigned cases and existing journal entries.';
    },
  },
  beforeUnmount() {
    this.sync_modal_body_class(false);
  },
  methods: {
    format_column_day,
    format_hours,
    reset_modal_state() {
      this.active_modal = '';
      this.add_case_row_requested = false;
      this.sync_modal_body_class(false);
    },
    sync_modal_body_class(is_open) {
      if (typeof document === 'undefined') return;
      document.body.classList.toggle('case-owner-timesheet-modal-open', Boolean(is_open));
    },
    handle_add_case_evidence_change(event) {
      this.add_case_form = {
        ...this.add_case_form,
        evidence_files: Array.from(event?.target?.files || []),
      };
    },
    remove_add_case_evidence(index) {
      this.add_case_form = {
        ...this.add_case_form,
        evidence_files: (this.add_case_form.evidence_files || []).filter((_, file_index) => file_index !== index),
      };
      this.sync_add_case_evidence_input();
    },
    sync_add_case_evidence_input() {
      if (this.$refs?.add_case_evidence_input) {
        this.$refs.add_case_evidence_input.value = '';
      }
    },
    pending_evidence_count(row = {}, day_index = -1) {
      const date = week_dates(this.selected_week_start)[day_index];
      if (!date || !row?.key) {
        return 0;
      }

      const key = `${row.key}|${date_key(date)}`;
      return Array.isArray(this.pending_evidence?.[key]) ? this.pending_evidence[key].length : 0;
    },
    open_modal(modal_name) {
      this.active_modal = modal_name;
      this.sync_modal_body_class(true);
    },
    close_modal() {
      this.active_modal = '';
      this.sync_modal_body_class(false);
    },
    show_feedback_modal(title, message, tone = 'info') {
      this.feedback_modal = { title, message, tone };
      this.open_modal('feedback');
    },
    async load_time_entries() {
      this.loading_entries = true;
      this.entries_error = '';

      try {
        const [cases, profiles_response] = await Promise.all([
          fetch_case_owner_cases(this.project_id),
          fetch_requestor_entity_profiles({
            page: 1,
            page_size: 500,
            ...(this.project_id ? { project_id: this.project_id } : {}),
          }),
        ]);
        const profiles = Array.isArray(profiles_response?.data) ? profiles_response.data : [];
        const billable_rows = cases
          .filter((case_item) => case_item?.application_id)
          .map((case_item) => build_row(case_item, true));
        const base_row_keys = new Set(billable_rows.map((row) => row.key));
        const case_map = new Map(
          cases
            .filter((case_item) => case_item?.application_id)
            .map((case_item) => [Number(case_item.application_id), case_item])
        );
        const history_sources = profiles
          .map((profile) => {
            const application_id = application_id_from_profile(profile);
            if (!application_id) {
              return null;
            }

            return {
              application_id,
              case_item: historical_case_item(profile, case_map.get(Number(application_id))),
            };
          })
          .filter(Boolean)
          .filter((source, index, sources) =>
            sources.findIndex((candidate) => Number(candidate.application_id) === Number(source.application_id)) === index
          );
        const journal_sets = await Promise.all(
          history_sources
            .map(async ({ application_id, case_item }) => {
              try {
                const journals = await fetch_requestor_application_journals(application_id);
                return journals
                  .map((journal) => {
                    const review_event = review_event_from_journal(journal);
                    if (review_event) {
                      return {
                        row: null,
                        entry: null,
                        review_event,
                      };
                    }

                    if (!matches_current_handler(this, journal)) {
                      return null;
                    }

                    const row = build_row(case_item, journal_is_billable(journal), {
                      activity_label: journal_activity(journal),
                      display_title: case_item?.title,
                    });
                    return {
                      row,
                      entry: normalize_entry(row, journal),
                      review_event: null,
                    };
                  })
                  .filter(Boolean);
              } catch {
                return [];
              }
            })
        );

        const journal_results = journal_sets.flat();
        const flattened = journal_results.filter((result) => result.entry);
        const review_events = journal_results
          .map((result) => result.review_event)
          .filter(Boolean)
          .filter((event, index, events) =>
            events.findIndex((candidate) =>
              candidate.status_key === event.status_key
              && date_key(candidate.week_start) === date_key(event.week_start)
              && String(candidate.created_at || '') === String(event.created_at || '')
            ) === index
          );
        const live_extra_rows = flattened
          .map((result) => result.row)
          .filter((row) => !base_row_keys.has(row.key))
          .filter((row, index, rows) => rows.findIndex((candidate) => candidate.key === row.key) === index);
        const stored_draft_rows = read_draft_rows(this.project_id)
          .filter((row) => !base_row_keys.has(row.key));
        const extra_rows = [...live_extra_rows, ...stored_draft_rows]
          .filter((row, index, rows) => rows.findIndex((candidate) => candidate.key === row.key) === index);

        this.case_rows = billable_rows;
        this.extra_rows = extra_rows;
        this.review_events = review_events;
        this.existing_entries = flattened
          .map((result) => result.entry)
          .sort((left, right) => right.date.getTime() - left.date.getTime());
        const available_months = this.months;
        this.selected_month = available_months.some((month) => month.value === this.selected_month)
          ? this.selected_month
          : available_months[0]?.value || '';
        this.pending_entries = read_pending_entries(this.project_id);
        this.pending_evidence = {};
      } catch (error) {
        this.case_rows = [];
        this.extra_rows = read_draft_rows(this.project_id);
        this.existing_entries = [];
        this.review_events = [];
        this.pending_entries = read_pending_entries(this.project_id);
        this.pending_evidence = {};
        this.entries_error = error?.response?.data?.message || error?.message || 'Failed to load time entries.';
      } finally {
        this.loading_entries = false;
      }
    },
    go_to_case_detail(case_code) {
      this.$router.push({ name: 'ViewCaseDetails', params: { caseCode: case_code } });
    },
    is_case_row(case_code) {
      return String(case_code || '').startsWith('C-');
    },
    previous_week() {
      this.selected_week_start = add_days(this.selected_week_start, -7);
    },
    next_week() {
      this.selected_week_start = add_days(this.selected_week_start, 7);
    },
    go_to_current_week() {
      this.selected_week_start = start_of_week(new Date());
    },
    edit_submitted_timesheet(sheet = null) {
      if (!sheet?.week_start) {
        return;
      }

      this.selected_week_start = start_of_week(new Date(sheet.week_start));
      this.active_tab = 'entry';

      const message = sheet.status_key === 'disputed'
        ? (sheet.comment || 'This timesheet was returned for revision. Update the week and resubmit it.')
        : sheet.status_key === 'approved'
          ? 'This approved timesheet is open in the weekly grid. Add a correction and resubmit if needed.'
          : 'This submitted timesheet is open in the weekly grid. You can add corrections before resubmitting.';

      this.show_feedback_modal('Editing submitted timesheet', message, sheet.status_key === 'disputed' ? 'warning' : 'info');
    },
    edit_cell(row, day_index) {
      const date = week_dates(this.selected_week_start)[day_index];
      const existing_hours = entry_sum_for_row_day(this.existing_entries, row.key, date);
      const pending_hours = pending_sum_for_row_day(this.pending_entries, row.key, date);
      const key = `${row.key}|${date_key(date)}`;
      this.editing_cell = {
        row,
        date,
        key,
        existing_hours,
        pending_hours,
        input: pending_hours ? String(pending_hours).replace('.', ',') : '',
      };
      this.open_modal('edit-cell');
    },
    save_cell_edit() {
      const value = Number(String(this.editing_cell.input || '').replace(',', '.').trim());
      if (!Number.isFinite(value) || value < 0) {
        this.show_feedback_modal('Invalid hours', 'Enter a valid number of hours greater than or equal to 0.', 'danger');
        return;
      }

      if (value === 0) {
        const next_entries = { ...this.pending_entries };
        const next_evidence = { ...this.pending_evidence };
        delete next_entries[this.editing_cell.key];
        delete next_evidence[this.editing_cell.key];
        this.pending_entries = next_entries;
        this.pending_evidence = next_evidence;
        write_pending_entries(this.project_id, this.pending_entries);
        this.close_modal();
        this.show_feedback_modal('Draft updated', 'The saved draft hours were cleared for this day.', 'success');
        return;
      }

      this.pending_entries = {
        ...this.pending_entries,
        [this.editing_cell.key]: Number(value.toFixed(2)),
      };
      this.close_modal();
      write_pending_entries(this.project_id, this.pending_entries);
      this.show_feedback_modal('Hours saved', 'Your draft timesheet entry was saved successfully.', 'success');
    },
    open_submit_week_modal() {
      const payloads = draft_payloads(this);
      if (!payloads.length) {
        this.show_feedback_modal('Nothing to submit', 'Add hours to at least one cell before submitting the week.', 'warning');
        return;
      }

      if (!payloads.every((payload) => payload.application_id && payload.user_id)) {
        this.show_feedback_modal('Submission blocked', 'Some drafted time entries are missing a case reference or current user.', 'danger');
        return;
      }

      this.submit_review_target = 'coordinator';
      this.open_modal('submit-week');
    },
    async submit_week() {
      const payloads = draft_payloads(this);
      if (!payloads.length || !payloads.every((payload) => payload.application_id && payload.user_id)) {
        this.close_modal();
        this.open_submit_week_modal();
        return;
      }

      this.submitting_week = true;
      try {
        await Promise.all(payloads.map((payload) => create_requestor_application_journal(payload)));
        this.pending_entries = {};
        write_pending_entries(this.project_id, this.pending_entries);
        this.extra_rows = [];
        write_draft_rows(this.project_id, []);
        this.close_modal();
        this.show_feedback_modal(
          'Timesheet submitted',
          `Time entries submitted successfully and marked for review by the ${this.submit_review_target_label}.`,
          'success'
        );
        await this.load_time_entries();
      } catch (error) {
        this.close_modal();
        this.show_feedback_modal(
          'Submission failed',
          error?.response?.data?.message || error?.message || 'Failed to submit time entries.',
          'danger'
        );
      } finally {
        this.submitting_week = false;
      }
    },
    add_case_row() {
      if (!this.case_rows.length) {
        this.show_feedback_modal('No cases available', 'No assigned cases are available for additional time rows yet.', 'warning');
        return;
      }

      this.add_case_form = {
        application_id: String(this.case_rows[0]?.application_id || ''),
        activity: '',
        billable: false,
        day_key: date_key(this.selected_week_start),
        hours: '',
        evidence_files: [],
      };
      this.add_case_row_requested = true;
      this.open_modal('add-case-row');
    },
    save_case_row() {
      const application_id = String(this.add_case_form.application_id || '').trim();
      const activity = normalize_text(this.add_case_form.activity);
      const day_key = String(this.add_case_form.day_key || '').trim();
      const hours = Number(String(this.add_case_form.hours || '').replace(',', '.').trim());
      if (!application_id) {
        this.show_feedback_modal('Case required', 'Select an existing case before adding a row.', 'warning');
        return;
      }
      if (!activity) {
        this.show_feedback_modal('Activity required', 'Enter the activity for this time row.', 'warning');
        return;
      }
      if (!day_key) {
        this.show_feedback_modal('Day required', 'Select the day you want to log time against.', 'warning');
        return;
      }
      if (!Number.isFinite(hours) || hours <= 0) {
        this.show_feedback_modal('Hours required', 'Enter valid hours greater than 0 for this time entry.', 'warning');
        return;
      }

      const matched_row = this.case_rows.find((row) => String(row.application_id) === application_id);
      if (!matched_row) {
        this.show_feedback_modal('Case not found', 'That case is no longer available in your current case list.', 'danger');
        return;
      }

      const existing_row = this.row_definitions.find((row) =>
        String(row.application_id) === application_id
        && Boolean(row.billable) === Boolean(this.add_case_form.billable)
      );

      const next_row = build_row({
        application_id: matched_row.application_id,
        case_code: matched_row.case_code,
        title: matched_row.title,
      }, this.add_case_form.billable, {
        activity_label: activity,
        display_title: matched_row.title,
      });

      const target_row = existing_row || next_row;

      if (!existing_row) {
        this.extra_rows = [...this.extra_rows, next_row];
        write_draft_rows(this.project_id, this.extra_rows);
      }

      const pending_key = `${target_row.key}|${day_key}`;
      this.pending_entries = {
        ...this.pending_entries,
        [pending_key]: Number(hours.toFixed(2)),
      };
      this.pending_evidence = {
        ...this.pending_evidence,
        [pending_key]: Array.isArray(this.add_case_form.evidence_files) ? this.add_case_form.evidence_files : [],
      };
      this.sync_add_case_evidence_input();
      this.close_modal();
      write_pending_entries(this.project_id, this.pending_entries);
      if (existing_row) {
        write_draft_rows(this.project_id, this.extra_rows);
      }
      this.show_feedback_modal('Draft saved', 'The case row hours and evidence were saved for submission.', 'success');
    }
  },
  watch: {
    '$route.fullPath'() {
      this.reset_modal_state();
    },
    project_id() {
      this.load_time_entries();
    }
  }
};
